import type { OpTriage } from "../../lib/api";
import { shortAssetId } from "../../lib/format";
import { OnchainPlan } from "./plan/OnchainPlan";

/**
 * Per-op triage rendered below the failure banner. The backend
 * re-runs each input op in isolation when a multi-op bundle reverts,
 * so we can show "which intent specifically is broken." Green check
 * for ops that would have worked alone; red cross for ops that revert.
 */
export function TriageList({ triage }: { triage: OpTriage[] }) {
	const failing = triage.filter((t) => !t.ok);
	const ok = triage.filter((t) => t.ok);
	return (
		<div className="space-y-1 rounded-md border border-slate-700/60 bg-slate-900/40 p-2">
			<p className="font-semibold uppercase tracking-wide text-slate-200">
				Per-op triage ({ok.length} ok / {failing.length} would revert)
			</p>
			<ul className="space-y-1">
				{triage.map((t) => (
					<li
						key={t.assetId}
						className="flex items-start gap-2 font-mono text-[11px] leading-tight"
					>
						<span
							className={t.ok ? "text-emerald-300" : "text-rose-300"}
							aria-hidden
						>
							{t.ok ? "✓" : "✗"}
						</span>
						<div className="min-w-0 flex-1 space-y-1">
							<p className="text-slate-200">{shortAssetId(t.assetId, 28)}</p>
							{!t.ok && t.reason && (
								<p className="break-words text-rose-300/80">{t.reason}</p>
							)}
							{/* Per-op steps include the swap prelude (if any) the
							    planner emitted for this asset in isolation. Without
							    this row the user can't tell whether the swap or
							    the invest is what reverted. */}
							{t.steps && t.steps.length > 0 && (
								<div className="rounded-sm border border-slate-700/60 bg-slate-950/40 p-1.5">
									<OnchainPlan steps={t.steps} />
								</div>
							)}
						</div>
					</li>
				))}
			</ul>
			{failing.length > 0 && ok.length > 0 && (
				<p className="text-slate-400">
					Remove the ✗ {failing.length === 1 ? "op" : "ops"} from the plan and
					re-simulate — the remaining {ok.length}{" "}
					{ok.length === 1 ? "op" : "ops"} would broadcast cleanly.
				</p>
			)}
		</div>
	);
}
